export type LoanPurpose = 'Purchase' | 'Refinance' | 'HELOC' | 'Cash-Out Refi'
export type LeadStatus =
  | 'New'
  | 'Contacted'
  | 'Working'
  | 'Qualified'
  | 'Unqualified'
export type LeadSource =
  | 'Zillow'
  | 'Website'
  | 'Referral'
  | 'Bankrate'
  | 'Realtor.com'
export type AnomalyType =
  | 'Round Robin Queue'
  | 'Missing Campaign'
  | 'Madison Handoff Incomplete'
  | 'No Issues'

export interface CampaignDripStep {
  step: number
  name: string
  offsetDays: number
  description: string
}

// Canonical drip step schedules keyed by campaign name.
// Each step's scheduled date is computed as enrolledDate + offsetDays.
export const CAMPAIGN_DRIP_SCHEDULES: Record<string, CampaignDripStep[]> = {
  'First-Time Homebuyer': [
    {
      step: 1,
      name: 'Day 1 — Welcome Series',
      offsetDays: 0,
      description: 'Welcome email + intro to homebuying journey',
    },
    {
      step: 2,
      name: 'Day 3 — Education',
      offsetDays: 3,
      description: 'First-time buyer guide and FAQ',
    },
    {
      step: 3,
      name: 'Day 7 — Follow Up',
      offsetDays: 7,
      description: 'Loan officer outreach and pre-approval CTA',
    },
    {
      step: 4,
      name: 'Day 14 — Re-engage',
      offsetDays: 14,
      description: 'Market insights for first-time buyers',
    },
    {
      step: 5,
      name: 'Day 30 — Long-term Check In',
      offsetDays: 30,
      description: 'Monthly check-in and rate update',
    },
  ],
  'Refi Rate Drop Alert': [
    {
      step: 1,
      name: 'Day 1 — Welcome Series',
      offsetDays: 0,
      description: 'Rate drop notification + savings estimate',
    },
    {
      step: 2,
      name: 'Day 3 — Education',
      offsetDays: 3,
      description: 'How refinancing works',
    },
    {
      step: 3,
      name: 'Day 7 — Follow Up',
      offsetDays: 7,
      description: 'Loan officer outreach',
    },
    {
      step: 4,
      name: 'Day 14 — Re-engage',
      offsetDays: 14,
      description: 'Updated rate alert + application CTA',
    },
  ],
  'HELOC Education': [
    {
      step: 1,
      name: 'Day 1 — Welcome Series',
      offsetDays: 0,
      description: 'HELOC basics + how it works',
    },
    {
      step: 2,
      name: 'Day 5 — Education',
      offsetDays: 5,
      description: 'HELOC vs Cash-Out Refi comparison',
    },
    {
      step: 3,
      name: 'Day 10 — Follow Up',
      offsetDays: 10,
      description: 'Loan officer outreach',
    },
    {
      step: 4,
      name: 'Day 21 — Re-engage',
      offsetDays: 21,
      description: 'Use cases and customer stories',
    },
  ],
  'Cash-Out Refi Nurture': [
    {
      step: 1,
      name: 'Day 1 — Welcome Series',
      offsetDays: 0,
      description: 'Welcome + equity overview',
    },
    {
      step: 2,
      name: 'Day 4 — Education',
      offsetDays: 4,
      description: 'Cash-Out Refi guide and use cases',
    },
    {
      step: 3,
      name: 'Day 9 — Follow Up',
      offsetDays: 9,
      description: 'Loan officer outreach',
    },
    {
      step: 4,
      name: 'Day 18 — Re-engage',
      offsetDays: 18,
      description: 'Equity calculator and rate update',
    },
  ],
  'Purchase Nurture 30-Day': [
    {
      step: 1,
      name: 'Day 1 — Welcome Series',
      offsetDays: 0,
      description: 'Welcome email + intro',
    },
    {
      step: 2,
      name: 'Day 5 — Education',
      offsetDays: 5,
      description: 'Purchase process overview',
    },
    {
      step: 3,
      name: 'Day 12 — Follow Up',
      offsetDays: 12,
      description: 'Pre-approval CTA',
    },
    {
      step: 4,
      name: 'Day 20 — Re-engage',
      offsetDays: 20,
      description: 'Local market update',
    },
    {
      step: 5,
      name: 'Day 30 — Long-term Check In',
      offsetDays: 30,
      description: 'Monthly check-in',
    },
  ],
  'Long-Term Nurture': [
    {
      step: 1,
      name: 'Month 1 — Check In',
      offsetDays: 30,
      description: 'Monthly market update',
    },
    {
      step: 2,
      name: 'Month 2 — Check In',
      offsetDays: 60,
      description: 'Rate update and re-engagement',
    },
    {
      step: 3,
      name: 'Month 3 — Check In',
      offsetDays: 90,
      description: 'Quarterly check-in',
    },
    {
      step: 4,
      name: 'Month 6 — Re-engage',
      offsetDays: 180,
      description: 'Semi-annual rate and equity update',
    },
  ],
}

export interface ResolvedDripStep extends CampaignDripStep {
  scheduledDate: string
  status: 'Sent' | 'Pending'
}

export function getCampaignDripSchedule(lead: Lead): ResolvedDripStep[] | null {
  if (
    !lead.campaignEnrollment.enrolled ||
    !lead.campaignEnrollment.campaignName
  ) {
    return null
  }
  const steps = CAMPAIGN_DRIP_SCHEDULES[lead.campaignEnrollment.campaignName]
  if (!steps) return null
  const enrolledDate = lead.campaignEnrollment.enrolledDate
    ? new Date(lead.campaignEnrollment.enrolledDate)
    : new Date()
  const now = new Date()
  return steps.map((step) => {
    const scheduled = new Date(enrolledDate)
    scheduled.setDate(scheduled.getDate() + step.offsetDays)
    return {
      ...step,
      scheduledDate: scheduled.toISOString(),
      status: scheduled <= now ? 'Sent' : 'Pending',
    }
  })
}

export interface Lead {
  id: string
  name: string
  createdDate: string
  owner: string
  loanPurpose: LoanPurpose
  status: LeadStatus
  source: LeadSource
  campaignEnrollment: {
    enrolled: boolean
    campaignName?: string
    status?: string
    enrolledDate?: string
    expectedCampaign?: string
  }
  scheduledDrip: {
    scheduled: boolean
    status?: string
    scheduledDate?: string
    configName?: string
  }
  anomalies: AnomalyType[]
  propertyState: string
  sendToMadison: boolean
  madisonHandoffSmsSent: boolean
}

// Date helpers — produce a consistent "now" at midday so day comparisons are stable
const NOW = new Date()
const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const endOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}
const daysAgo = (n: number) => {
  const d = new Date(NOW)
  d.setDate(d.getDate() - n)
  d.setHours(10, 0, 0, 0)
  return d.toISOString()
}
const daysFromNow = (n: number) => {
  const d = new Date(NOW)
  d.setDate(d.getDate() + n)
  return d.toISOString()
}

// Day offsets used to land leads in different timeframe buckets.
// Week buckets assume Monday as the start of the week.
const dayOfWeek = NOW.getDay() // 0 = Sun, 1 = Mon, ...
const daysIntoWeek = (dayOfWeek + 6) % 7 // 0 = Mon
const daysIntoMonth = NOW.getDate() - 1

// Pre-compute helpful offsets for varied "earlier this week" / "earlier this month"
const EARLIER_THIS_WEEK = Math.min(daysIntoWeek, 3) // 0..3 days ago, but still this week
const EARLIER_THIS_MONTH = Math.min(daysIntoMonth, 18) // earlier this month, but past last week
const LAST_WEEK_OFFSET = daysIntoWeek + 3 // somewhere in last week
const BEYOND_MONTH_OFFSET = daysIntoMonth + 5 // before this month

export const UNLICENSED_STATES = [
  'AK',
  'AR',
  'DE',
  'HI',
  'KS',
  'ME',
  'MO',
  'MT',
  'NE',
  'NH',
  'NM',
  'NV',
  'NY',
  'ND',
  'OK',
  'RI',
  'SD',
  'UT',
  'VT',
  'WY',
] as const

const UNLICENSED_STATES_SET = new Set<string>(UNLICENSED_STATES)

export function isUnlicensedState(state: string): boolean {
  return UNLICENSED_STATES_SET.has(state)
}

// Madison Queue is a subset of unlicensed states that get handed off to a partner.
export const MADISON_STATES = [
  'AR',
  'KS',
  'ME',
  'MO',
  'MT',
  'NE',
  'NH',
  'NM',
  'NY',
  'ND',
  'OK',
  'RI',
  'SD',
  'VT',
  'WY',
] as const

const MADISON_STATES_SET = new Set<string>(MADISON_STATES)

export function isMadisonState(state: string): boolean {
  return MADISON_STATES_SET.has(state)
}

// Returns the anomalies that should actually be displayed/counted for a lead.
// Madison-state leads have a different anomaly model: the Round Robin Queue
// and Missing Campaign checks do not apply. Instead, we only flag a handoff
// problem if either the "Send to Madison" or "Madison Handoff SMS Sent"
// checkbox is missing.
export function getEffectiveAnomalies(lead: Lead): AnomalyType[] {
  if (isMadisonState(lead.propertyState)) {
    const handoffComplete = lead.sendToMadison && lead.madisonHandoffSmsSent
    return handoffComplete ? ['No Issues'] : ['Madison Handoff Incomplete']
  }
  return lead.anomalies
}

// Property state assigned by lead id so we can intentionally mix licensed and unlicensed states.
const PROPERTY_STATE_BY_ID: Record<string, string> = {
  // Today
  'L-1001': 'CA',
  'L-1002': 'NY', // unlicensed
  'L-1003': 'NV', // unlicensed
  'L-1004': 'TX',
  'L-1005': 'OK', // unlicensed
  'L-1006': 'FL',
  'L-1007': 'NM', // unlicensed
  // Yesterday
  'L-1101': 'GA',
  'L-1102': 'NH', // unlicensed
  'L-1103': 'WA',
  'L-1104': 'UT', // unlicensed
  'L-1105': 'IL',
  // Earlier this week
  'L-1201': 'VT', // unlicensed
  'L-1202': 'AZ',
  'L-1203': 'MO', // unlicensed
  // Last week
  'L-1301': 'CO',
  'L-1302': 'NC',
  'L-1303': 'PA',
  'L-1304': 'WY', // unlicensed
  'L-1305': 'OH',
  'L-1306': 'RI', // unlicensed
  // Earlier this month
  'L-1401': 'VA',
  'L-1402': 'MA',
  'L-1403': 'AR', // unlicensed
  'L-1404': 'MI',
  'L-1405': 'AK', // unlicensed
  // Beyond this month
  'L-1501': 'KS', // unlicensed
  'L-1502': 'TN',
}

const RAW_MOCK_LEADS: Omit<Lead, 'propertyState'>[] = [
  // ─────────────────────────────────────────────────
  // TODAY (created today)
  // ─────────────────────────────────────────────────
  {
    id: 'L-1001',
    name: 'Sarah Jenkins',
    createdDate: daysAgo(0),
    owner: 'Jamie Chen',
    loanPurpose: 'Purchase',
    status: 'New',
    source: 'Zillow',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'First-Time Homebuyer',
      status: 'Active',
      enrolledDate: daysAgo(0),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Pending',
      scheduledDate: daysFromNow(1),
      configName: 'Day 1 — Welcome Series',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1002',
    name: 'Michael Rodriguez',
    createdDate: daysAgo(0),
    owner: 'Round Robin Queue',
    loanPurpose: 'Refinance',
    status: 'New',
    source: 'Bankrate',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Refi Rate Drop Alert',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1003',
    name: 'Christopher Lee',
    createdDate: daysAgo(0),
    owner: 'Round Robin Queue',
    loanPurpose: 'HELOC',
    status: 'New',
    source: 'Bankrate',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'HELOC Education',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1004',
    name: 'Brian Clark',
    createdDate: daysAgo(0),
    owner: 'Alex Rivera',
    loanPurpose: 'Refinance',
    status: 'New',
    source: 'Zillow',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Refi Rate Drop Alert',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Missing Campaign'],
  },
  {
    id: 'L-1005',
    name: 'Justin Allen',
    createdDate: daysAgo(0),
    owner: 'Round Robin Queue',
    loanPurpose: 'Purchase',
    status: 'New',
    source: 'Referral',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'First-Time Homebuyer',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1006',
    name: 'Olivia Park',
    createdDate: daysAgo(0),
    owner: 'Priya Patel',
    loanPurpose: 'Purchase',
    status: 'Contacted',
    source: 'Website',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'First-Time Homebuyer',
      status: 'Active',
      enrolledDate: daysAgo(0),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Pending',
      scheduledDate: daysFromNow(2),
      configName: 'Day 3 — Education',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1007',
    name: 'Noah Bennett',
    createdDate: daysAgo(0),
    owner: 'Jamie Chen',
    loanPurpose: 'Cash-Out Refi',
    status: 'New',
    source: 'Realtor.com',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'Cash-Out Refi Nurture',
      status: 'Active',
      enrolledDate: daysAgo(0),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Pending',
      scheduledDate: daysFromNow(1),
      configName: 'Day 1 — Welcome Series',
    },
    anomalies: ['No Issues'],
  },

  // ─────────────────────────────────────────────────
  // YESTERDAY
  // ─────────────────────────────────────────────────
  {
    id: 'L-1101',
    name: 'Emily Davis',
    createdDate: daysAgo(1),
    owner: 'Alex Rivera',
    loanPurpose: 'HELOC',
    status: 'Contacted',
    source: 'Website',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'HELOC Education',
      status: 'Active',
      enrolledDate: daysAgo(1),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Sent',
      scheduledDate: daysAgo(1),
      configName: 'Day 1 — Welcome Series',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1102',
    name: 'David Smith',
    createdDate: daysAgo(1),
    owner: 'Round Robin Queue',
    loanPurpose: 'Cash-Out Refi',
    status: 'New',
    source: 'Referral',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Cash-Out Refi Nurture',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1103',
    name: 'Ashley White',
    createdDate: daysAgo(1),
    owner: 'Jamie Chen',
    loanPurpose: 'Cash-Out Refi',
    status: 'Contacted',
    source: 'Referral',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'Cash-Out Refi Nurture',
      status: 'Active',
      enrolledDate: daysAgo(1),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Sent',
      scheduledDate: daysAgo(1),
      configName: 'Day 1 — Welcome Series',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1104',
    name: 'Samantha Lewis',
    createdDate: daysAgo(1),
    owner: 'Round Robin Queue',
    loanPurpose: 'Purchase',
    status: 'New',
    source: 'Realtor.com',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Purchase Nurture 30-Day',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1105',
    name: 'Liam Foster',
    createdDate: daysAgo(1),
    owner: 'Priya Patel',
    loanPurpose: 'Refinance',
    status: 'Working',
    source: 'Bankrate',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Refi Rate Drop Alert',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Missing Campaign'],
  },

  // ─────────────────────────────────────────────────
  // EARLIER THIS WEEK (still within current week, but not today/yesterday)
  // ─────────────────────────────────────────────────
  ...(EARLIER_THIS_WEEK >= 2
    ? [
        {
          id: 'L-1201',
          name: 'Jessica Taylor',
          createdDate: daysAgo(EARLIER_THIS_WEEK),
          owner: 'Jamie Chen',
          loanPurpose: 'Purchase' as LoanPurpose,
          status: 'Working' as LeadStatus,
          source: 'Realtor.com' as LeadSource,
          campaignEnrollment: {
            enrolled: false,
            expectedCampaign: 'Purchase Nurture 30-Day',
          },
          scheduledDrip: { scheduled: false },
          anomalies: ['Missing Campaign'] as AnomalyType[],
        },
        {
          id: 'L-1202',
          name: 'Matthew Harris',
          createdDate: daysAgo(EARLIER_THIS_WEEK),
          owner: 'Marcus Johnson',
          loanPurpose: 'Purchase' as LoanPurpose,
          status: 'Working' as LeadStatus,
          source: 'Website' as LeadSource,
          campaignEnrollment: {
            enrolled: true,
            campaignName: 'First-Time Homebuyer',
            status: 'Active',
            enrolledDate: daysAgo(EARLIER_THIS_WEEK),
          },
          scheduledDrip: {
            scheduled: true,
            status: 'Pending',
            scheduledDate: daysFromNow(1),
            configName: 'Day 3 — Education',
          },
          anomalies: ['No Issues'] as AnomalyType[],
        },
        {
          id: 'L-1203',
          name: 'Rachel Hall',
          createdDate: daysAgo(EARLIER_THIS_WEEK),
          owner: 'Marcus Johnson',
          loanPurpose: 'Cash-Out Refi' as LoanPurpose,
          status: 'Contacted' as LeadStatus,
          source: 'Bankrate' as LeadSource,
          campaignEnrollment: {
            enrolled: false,
            expectedCampaign: 'Cash-Out Refi Nurture',
          },
          scheduledDrip: { scheduled: false },
          anomalies: ['Missing Campaign'] as AnomalyType[],
        },
      ]
    : []),

  // ─────────────────────────────────────────────────
  // LAST WEEK
  // ─────────────────────────────────────────────────
  {
    id: 'L-1301',
    name: 'Amanda Martinez',
    createdDate: daysAgo(LAST_WEEK_OFFSET),
    owner: 'Alex Rivera',
    loanPurpose: 'Purchase',
    status: 'Unqualified',
    source: 'Zillow',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'Long-Term Nurture',
      status: 'Active',
      enrolledDate: daysAgo(LAST_WEEK_OFFSET),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Pending',
      scheduledDate: daysFromNow(14),
      configName: 'Month 1 — Check In',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1302',
    name: 'Kevin Walker',
    createdDate: daysAgo(LAST_WEEK_OFFSET),
    owner: 'Jamie Chen',
    loanPurpose: 'HELOC',
    status: 'Qualified',
    source: 'Website',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'HELOC Education',
      status: 'Completed',
      enrolledDate: daysAgo(LAST_WEEK_OFFSET),
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1303',
    name: 'Robert Wilson',
    createdDate: daysAgo(LAST_WEEK_OFFSET + 1),
    owner: 'Marcus Johnson',
    loanPurpose: 'Refinance',
    status: 'Qualified',
    source: 'Website',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'Refi Rate Drop Alert',
      status: 'Completed',
      enrolledDate: daysAgo(LAST_WEEK_OFFSET + 1),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Pending',
      scheduledDate: daysFromNow(3),
      configName: 'Day 7 — Follow Up',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1304',
    name: 'Tina Brooks',
    createdDate: daysAgo(LAST_WEEK_OFFSET + 2),
    owner: 'Round Robin Queue',
    loanPurpose: 'Refinance',
    status: 'New',
    source: 'Bankrate',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Refi Rate Drop Alert',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Round Robin Queue', 'Missing Campaign'],
  },
  {
    id: 'L-1305',
    name: 'Daniel Kim',
    createdDate: daysAgo(LAST_WEEK_OFFSET + 2),
    owner: 'Priya Patel',
    loanPurpose: 'HELOC',
    status: 'Contacted',
    source: 'Referral',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'HELOC Education',
      status: 'Active',
      enrolledDate: daysAgo(LAST_WEEK_OFFSET + 2),
    },
    scheduledDrip: {
      scheduled: true,
      status: 'Sent',
      scheduledDate: daysAgo(LAST_WEEK_OFFSET + 1),
      configName: 'Day 1 — Welcome Series',
    },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1306',
    name: 'Megan Foster',
    createdDate: daysAgo(LAST_WEEK_OFFSET + 3),
    owner: 'Alex Rivera',
    loanPurpose: 'Purchase',
    status: 'Working',
    source: 'Zillow',
    campaignEnrollment: {
      enrolled: false,
      expectedCampaign: 'Purchase Nurture 30-Day',
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['Missing Campaign'],
  },

  // ─────────────────────────────────────────────────
  // EARLIER THIS MONTH (within this calendar month, before last week)
  // ─────────────────────────────────────────────────
  ...(EARLIER_THIS_MONTH > LAST_WEEK_OFFSET + 3
    ? [
        {
          id: 'L-1401',
          name: 'Carlos Mendez',
          createdDate: daysAgo(EARLIER_THIS_MONTH),
          owner: 'Marcus Johnson',
          loanPurpose: 'Purchase' as LoanPurpose,
          status: 'Qualified' as LeadStatus,
          source: 'Realtor.com' as LeadSource,
          campaignEnrollment: {
            enrolled: true,
            campaignName: 'First-Time Homebuyer',
            status: 'Completed',
            enrolledDate: daysAgo(EARLIER_THIS_MONTH),
          },
          scheduledDrip: {
            scheduled: true,
            status: 'Sent',
            scheduledDate: daysAgo(EARLIER_THIS_MONTH - 1),
            configName: 'Day 7 — Follow Up',
          },
          anomalies: ['No Issues'] as AnomalyType[],
        },
        {
          id: 'L-1402',
          name: 'Hannah Reyes',
          createdDate: daysAgo(EARLIER_THIS_MONTH - 1),
          owner: 'Jamie Chen',
          loanPurpose: 'Refinance' as LoanPurpose,
          status: 'Contacted' as LeadStatus,
          source: 'Website' as LeadSource,
          campaignEnrollment: {
            enrolled: true,
            campaignName: 'Refi Rate Drop Alert',
            status: 'Active',
            enrolledDate: daysAgo(EARLIER_THIS_MONTH - 1),
          },
          scheduledDrip: {
            scheduled: true,
            status: 'Pending',
            scheduledDate: daysFromNow(2),
            configName: 'Day 14 — Re-engage',
          },
          anomalies: ['No Issues'] as AnomalyType[],
        },
        {
          id: 'L-1403',
          name: 'Anthony Reed',
          createdDate: daysAgo(EARLIER_THIS_MONTH - 2),
          owner: 'Round Robin Queue',
          loanPurpose: 'Cash-Out Refi' as LoanPurpose,
          status: 'New' as LeadStatus,
          source: 'Bankrate' as LeadSource,
          campaignEnrollment: {
            enrolled: false,
            expectedCampaign: 'Cash-Out Refi Nurture',
          },
          scheduledDrip: { scheduled: false },
          anomalies: ['Round Robin Queue', 'Missing Campaign'] as AnomalyType[],
        },
        {
          id: 'L-1404',
          name: 'Vanessa Cole',
          createdDate: daysAgo(EARLIER_THIS_MONTH - 3),
          owner: 'Priya Patel',
          loanPurpose: 'HELOC' as LoanPurpose,
          status: 'Qualified' as LeadStatus,
          source: 'Website' as LeadSource,
          campaignEnrollment: {
            enrolled: true,
            campaignName: 'HELOC Education',
            status: 'Completed',
            enrolledDate: daysAgo(EARLIER_THIS_MONTH - 3),
          },
          scheduledDrip: { scheduled: false },
          anomalies: ['No Issues'] as AnomalyType[],
        },
        {
          id: 'L-1405',
          name: 'Derek Nguyen',
          createdDate: daysAgo(EARLIER_THIS_MONTH - 4),
          owner: 'Alex Rivera',
          loanPurpose: 'Purchase' as LoanPurpose,
          status: 'Unqualified' as LeadStatus,
          source: 'Zillow' as LeadSource,
          campaignEnrollment: {
            enrolled: true,
            campaignName: 'Long-Term Nurture',
            status: 'Active',
            enrolledDate: daysAgo(EARLIER_THIS_MONTH - 4),
          },
          scheduledDrip: {
            scheduled: true,
            status: 'Pending',
            scheduledDate: daysFromNow(20),
            configName: 'Month 2 — Check In',
          },
          anomalies: ['No Issues'] as AnomalyType[],
        },
      ]
    : []),

  // ─────────────────────────────────────────────────
  // BEYOND THIS MONTH (older — should not appear in any preset timeframe)
  // ─────────────────────────────────────────────────
  {
    id: 'L-1501',
    name: 'Patricia Greene',
    createdDate: daysAgo(BEYOND_MONTH_OFFSET),
    owner: 'Marcus Johnson',
    loanPurpose: 'Refinance',
    status: 'Unqualified',
    source: 'Bankrate',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'Long-Term Nurture',
      status: 'Completed',
      enrolledDate: daysAgo(BEYOND_MONTH_OFFSET),
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['No Issues'],
  },
  {
    id: 'L-1502',
    name: 'George Thompson',
    createdDate: daysAgo(BEYOND_MONTH_OFFSET + 7),
    owner: 'Jamie Chen',
    loanPurpose: 'Purchase',
    status: 'Qualified',
    source: 'Referral',
    campaignEnrollment: {
      enrolled: true,
      campaignName: 'First-Time Homebuyer',
      status: 'Completed',
      enrolledDate: daysAgo(BEYOND_MONTH_OFFSET + 7),
    },
    scheduledDrip: { scheduled: false },
    anomalies: ['No Issues'],
  },
]

// Deterministic-ish booleans for the Madison handoff checkboxes so we get a
// realistic mix: most are sent, fewer have the SMS confirmed.
function hashId(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return h
}

export const mockLeads: Lead[] = RAW_MOCK_LEADS.map((l) => {
  const propertyState = PROPERTY_STATE_BY_ID[l.id] ?? 'CA'
  const inMadison = MADISON_STATES_SET.has(propertyState)
  const h = hashId(l.id)
  return {
    ...l,
    propertyState,
    // Only Madison-state leads can have these flags set; non-Madison leads default to false.
    sendToMadison: inMadison ? h % 4 !== 0 : false, // ~75% sent
    madisonHandoffSmsSent: inMadison ? h % 3 === 0 : false, // ~33% SMS confirmed
  }
})

// ─────────────────────────────────────────────────
// Timeframe filtering
// ─────────────────────────────────────────────────
export type TimeframeKey =
  | 'Today'
  | 'Yesterday'
  | 'This Week'
  | 'Last Week'
  | 'This Month'
  | 'Custom Range'

export function getTimeframeRange(timeframe: TimeframeKey): {
  start: Date
  end: Date
} {
  const now = new Date()
  const today = startOfDay(now)
  const dow = now.getDay() // 0 Sun..6 Sat
  const offsetToMonday = (dow + 6) % 7 // Mon=0

  const startOfThisWeek = new Date(today)
  startOfThisWeek.setDate(startOfThisWeek.getDate() - offsetToMonday)

  const startOfLastWeek = new Date(startOfThisWeek)
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
  const endOfLastWeek = new Date(startOfThisWeek)
  endOfLastWeek.setMilliseconds(-1)

  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  switch (timeframe) {
    case 'Today':
      return { start: today, end: endOfDay(now) }
    case 'Yesterday': {
      const y = new Date(today)
      y.setDate(y.getDate() - 1)
      return { start: y, end: endOfDay(y) }
    }
    case 'This Week':
      return { start: startOfThisWeek, end: endOfDay(now) }
    case 'Last Week':
      return { start: startOfLastWeek, end: endOfLastWeek }
    case 'This Month':
      return { start: startOfThisMonth, end: endOfDay(now) }
    case 'Custom Range':
    default:
      // For Custom Range with no dates set, default to a wide window
      return { start: new Date(0), end: endOfDay(now) }
  }
}

export function isInTimeframe(lead: Lead, timeframe: TimeframeKey): boolean {
  const { start, end } = getTimeframeRange(timeframe)
  const d = new Date(lead.createdDate)
  return d >= start && d <= end
}
